import { DateTime } from 'luxon';

import {
  AbstractControl,
  FormArray,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ValidatorsPlus } from '../../common/validators/plus.validator';
import { FormGroupEx } from '../models/form-group-ex';
import { isNullOrEmpty } from './common-functions';

/**
 * 客製化檢核
 *
 * @export
 * @class CustomValidators
 */
export class CustomValidators extends ValidatorsPlus {
  /**
   * 條件型檢核
   * 當條件成立時執行檢核方法
   * @static
   * @param {(ValidatorFn | null)} validator 檢核方法
   * @param {() => boolean} callback 條件 callback
   * @return {*}  {(ValidatorFn | null)}
   * @memberof CustomValidators
   */
  static enableValidatorWhen(
    validator: ValidatorFn | null,
    callback: () => boolean
  ): ValidatorFn | null {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (isNullOrEmpty(validator)) {
        return null;
      }

      const func = validator as ValidatorFn;
      if (callback()) {
        return func(ctrl);
      }
      return null;
    };
  }

  /**
   * 日期大於(等於)檢核
   *
   * @static
   * @param {Date} minDate 最小日期
   * @param {boolean} [orEqual=false] 是否加上等於
   * @return {*}  {(ValidatorFn | null)}
   * @memberof CustomValidators
   */
  static dateGreaterThan(minDate: Date, orEqual = false): ValidatorFn | null {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (isNullOrEmpty(ctrl.value)) {
        return null;
      }
      const targetDateVal = DateTime.fromJSDate(ctrl.value);
      const minDateVal = DateTime.fromJSDate(minDate);

      if (minDateVal.isValid === false) {
        throw new Error('Min date is invalid.');
      }

      if (targetDateVal.isValid === false) {
        return null;
      }

      const targetJSDate = targetDateVal
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .toJSDate();
      const minJSDate = minDateVal
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .toJSDate();

      if (orEqual) {
        return targetJSDate >= minJSDate
          ? null
          : { dateGreaterThanOrEqual: { minDate } };
      } else {
        return targetJSDate > minJSDate
          ? null
          : { dateGreaterThan: { minDate } };
      }
    };
  }

  /**
   * 日期小於(等於)檢核
   *
   * @static
   * @param {Date} maxDate 最大日期
   * @param {boolean} [orEqual=false] 是否加上等於
   * @return {*}  {(ValidatorFn | null)}
   * @memberof CustomValidators
   */
  static dateLessThan(maxDate: Date, orEqual = false): ValidatorFn | null {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (isNullOrEmpty(ctrl.value)) {
        return null;
      }
      const targetDateVal = DateTime.fromJSDate(ctrl.value);
      const maxDateVal = DateTime.fromJSDate(maxDate);

      if (maxDateVal.isValid === false) {
        throw new Error('Max date is invalid.');
      }

      if (targetDateVal.isValid === false) {
        return null;
      }

      const targetJSDate = targetDateVal
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .toJSDate();
      const maxJSDate = maxDateVal
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .toJSDate();

      if (orEqual) {
        return targetJSDate <= maxJSDate
          ? null
          : { dateLessThanOrEqual: { maxDate } };
      } else {
        return targetJSDate < maxJSDate ? null : { dateLessThan: { maxDate } };
      }
    };
  }

  /**
   * 重複值檢核
   *
   * @static
   * @template T
   * @param {T[]} values 值陣列
   * @return {*}  {(ValidatorFn | null)}
   * @memberof CustomValidators
   */
  static notDuplicated<T>(values: T[]): ValidatorFn | null {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (isNullOrEmpty(ctrl.value)) {
        return null;
      }
      return values.indexOf(ctrl.value) > -1 ? { duplicated: true } : null;
    };
  }

  /**
   * 送出時必填檢核
   *
   * @static
   * @param {AbstractControl} ctrl
   * @return {*}  {(ValidationErrors | null)}
   * @memberof CustomValidators
   */
  static requiredOnSubmit(ctrl: AbstractControl): ValidationErrors | null {
    if (isNullOrEmpty(ctrl)) return null;
    const formEx = getFormGroupEx(ctrl);
    return formEx?.submitted ? Validators.required(ctrl) : null;
  }
}

/**
 * 是否顯示 Form Control 檢核訊息
 *
 * @export
 * @param {(AbstractControl | null)} control Form Control
 * @param {boolean} submitted 表單是否送出
 * @return {*}  {boolean}
 */
export function showValidationMessage(
  control: AbstractControl | null,
  submitted: boolean
): boolean {
  if (isNullOrEmpty(control)) {
    return false;
  }
  const ctrl = control as AbstractControl;
  return (submitted || ctrl.touched) && ctrl.invalid;
}

/**
 * 將 AbstractControl 轉型成 FormArray
 *
 * @export
 * @param {(AbstractControl | null)} control
 * @return {*}  {(FormArray | null)}
 */
export function getAsFormArray(
  control: AbstractControl | null
): FormArray | null {
  if (isNullOrEmpty(control)) {
    return null;
  }
  const ctrl = control as FormArray;
  return ctrl;
}

/**
 * 找出對應的 FormGroupEx
 *
 * @export
 * @param {(AbstractControl | null)} control
 * @return {*}  {(FormGroupEx | null)}
 */
export function getFormGroupEx(
  control: AbstractControl | null
): FormGroupEx | null {
  if (isNullOrEmpty(control)) {
    return null;
  }
  if (isNullOrEmpty(control!.parent)) {
    return control instanceof FormGroupEx ? control : null;
  }
  return getFormGroupEx(control!.parent);
}
